// ============================================================================
// MonEquipePage — roster de mon equipe (L10, F-150..F-166) — /mon-equipe
// ============================================================================
// Portage du PoC (dashboard)/mon-equipe/page.tsx (SSR searchParams) au standard
// maison (SPA + useSearchParams) :
//   - garde d'entree SERVEUR (M-150) : GET /api/mon-equipe 403 pour employee ET
//     manager -> ecran de refus i18n (pattern TeamsForbidden E1b) ;
//   - portee SERVEUR (M-151) : la liste `teams` EST deja la portee du viewer —
//     le SPA ne re-filtre rien (lecon GA-05 : jamais recalculer l'autorisation) ;
//   - 4 KPI (F-158..F-161) : masse (agregat M-153), effectif, sondages (anneau
//     F-163), rappels ouverts ; cartes blanches, icone en pastille (jamais de
//     card coloree) ;
//   - cartes membres (F-162) -> drill-in /profile/{email}?tab=gestion ;
//   - objectifs d'equipe (F-164/F-165) + rappels (F-166).
//
// DIFFERES (fiches au rapport) :
//   - Logo equipe editable (F-157/F-171/F-172) : le blob Firebase n'est pas migre
//     (meme dette que l'avatar Profil) — pastille/fallback seulement, upload/reset
//     differes. On ne sert ni ne signe JAMAIS un path Firebase.

import { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import {
  PageHeader, EmptyState, StatusBadge, KpiCard, GsSegmented, SkeletonCard,
  QueryError, GS_COMPONENTS,
} from '@gs/ui';
import {
  Users, Wallet, Activity, BellRing, ShieldAlert, Target, Plus, Trash2,
  ArrowRight, HeartPulse, Check, Undo2, Download,
} from 'lucide-react';
import { ApiForbiddenError, getRegisteredAccessToken } from '@gs/api-client';
import { useAuth } from '@gs/auth';
import { useTranslation } from '../../i18n';
import { getInitials } from '../../lib/profile/initials';
import {
  useMonEquipe, useToggleReminder, useDeleteReminder,
  type RosterMember, type Reminder, type MonEquipeObjective,
} from '../../hooks/useMonEquipe';
import { useUpdateObjective, useDeleteObjective, teamErrorCode } from '../../hooks/useTeams';
import { NewTeamObjectiveDialog } from '../../components/mon-equipe/NewTeamObjectiveDialog';
import { NewReminderDialog } from '../../components/mon-equipe/NewReminderDialog';
import { ProgressRing } from '../../components/mon-equipe/ProgressRing';

/** M-156 : cycle VERBATIM pending -> in_progress -> done -> pending. */
const NEXT_STATUS: Record<MonEquipeObjective['status'], MonEquipeObjective['status']> = {
  pending: 'in_progress',
  in_progress: 'done',
  done: 'pending',
};

/** Ecran de refus M-150 — l'autorite est le 403 serveur (employee ET manager). */
export function MonEquipeForbidden() {
  const { t } = useTranslation();
  return (
    <EmptyState
      icon={<ShieldAlert size={48} aria-hidden="true" />}
      title={t('gest:monEquipe.forbidden.title')}
      description={t('gest:monEquipe.forbidden.description')}
    />
  );
}

/** Montant agrege en devise locale (M-153 — jamais un montant par personne). */
function formatMoney(locale: string, value: number): string {
  return new Intl.NumberFormat(locale.startsWith('en') ? 'en-CA' : 'fr-CA', {
    style: 'currency', currency: 'CAD', maximumFractionDigits: 0,
  }).format(value);
}

/**
 * Carte membre (F-162). M-154 : scores de pulse SEULEMENT — aucun `comment` (le
 * DTO serveur ne le porte meme pas). M-153 : aucun salaire.
 */
function MemberCard({ member, teamId }: { member: RosterMember; teamId: string | null }) {
  const { t } = useTranslation();
  const params = new URLSearchParams({ tab: 'gestion', from: 'mon-equipe' });
  if (teamId) params.set('fromTeam', teamId);

  return (
    <Link
      to={`/profile/${encodeURIComponent(member.email)}?${params.toString()}`}
      className={`${GS_COMPONENTS.card} flex touch-manipulation items-center gap-3 p-4 transition-shadow hover:shadow-md`}
    >
      <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-brand-50 text-meta font-semibold text-brand-700 dark:bg-brand-950 dark:text-brand-300">
        {getInitials(member.fullName, member.email)}
      </span>
      <div className="min-w-0 flex-1">
        <p className="truncate font-semibold text-slate-900 dark:text-slate-100">
          {member.fullName ?? member.email}
        </p>
        {member.jobTitle ? (
          <p className="truncate text-meta text-slate-500 dark:text-slate-400">{member.jobTitle}</p>
        ) : null}
        {/* M-154 : workload/energy seulement — le commentaire du pulse n'arrive jamais ici. */}
        {member.submitted && member.workloadScore != null && member.energyScore != null ? (
          <p className="mt-1 text-meta tabular-nums text-slate-500 dark:text-slate-400">
            {t('gest:monEquipe.roster.scores', {
              workload: member.workloadScore, energy: member.energyScore,
            })}
          </p>
        ) : null}
      </div>
      <div className="shrink-0">
        {member.submitted ? (
          <StatusBadge variant="success">{t('gest:monEquipe.roster.upToDate')}</StatusBadge>
        ) : (
          <StatusBadge variant="neutral">{t('gest:monEquipe.roster.awaiting')}</StatusBadge>
        )}
      </div>
    </Link>
  );
}

/** Ligne d'objectif d'equipe (F-164/F-165) — cycle M-156. */
function ObjectiveItem({
  objective, teamId, canWrite, onError,
}: {
  objective: MonEquipeObjective;
  teamId: string;
  canWrite: boolean;
  onError: (code: string) => void;
}) {
  const { t } = useTranslation();
  const update = useUpdateObjective(teamId);
  const remove = useDeleteObjective(teamId);

  const variant = objective.status === 'done' ? 'success' : objective.status === 'in_progress' ? 'warning' : 'neutral';

  return (
    <li className="flex items-center gap-3 border-b border-slate-100 py-3 last:border-0 dark:border-slate-700">
      <div className="min-w-0 flex-1">
        <p className="truncate text-field text-slate-900 dark:text-slate-100">{objective.title}</p>
        {objective.targetDate ? (
          <p className="text-meta text-slate-500 dark:text-slate-400">
            {t('gest:monEquipe.objectives.due', { date: objective.targetDate })}
          </p>
        ) : null}
      </div>
      <StatusBadge variant={variant}>{t(`gest:monEquipe.objectives.status.${objective.status}`)}</StatusBadge>
      {canWrite ? (
        <div className="flex shrink-0 items-center gap-1">
          <button
            type="button"
            aria-label={t('gest:monEquipe.objectives.advance')}
            title={t('gest:monEquipe.objectives.advance')}
            disabled={update.isPending}
            onClick={() =>
              update.mutate(
                {
                  id: objective.id,
                  payload: {
                    title: objective.title,
                    // M-156 : cycle VERBATIM (le serveur re-valide l'enum).
                    status: NEXT_STATUS[objective.status],
                    targetDate: objective.targetDate,
                  },
                },
                { onError: (err) => onError(teamErrorCode(err)) },
              )
            }
            className="touch-manipulation rounded-lg p-2 text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-600 dark:hover:bg-slate-700 dark:hover:text-slate-300"
          >
            {objective.status === 'done'
              ? <Undo2 size={16} aria-hidden="true" />
              : <Check size={16} aria-hidden="true" />}
          </button>
          <button
            type="button"
            aria-label={t('gest:monEquipe.objectives.delete')}
            title={t('gest:monEquipe.objectives.delete')}
            disabled={remove.isPending}
            onClick={() => remove.mutate(objective.id, { onError: (err) => onError(teamErrorCode(err)) })}
            className="touch-manipulation rounded-lg p-2 text-slate-400 transition-colors hover:bg-red-50 hover:text-red-600 dark:hover:bg-red-950 dark:hover:text-red-400"
          >
            <Trash2 size={16} aria-hidden="true" />
          </button>
        </div>
      ) : null}
    </li>
  );
}

/** Ligne de rappel (F-166) — ownership M-155 re-verifiee serveur a chaque ecriture. */
function ReminderItem({ reminder, onError }: { reminder: Reminder; onError: (code: string) => void }) {
  const { t } = useTranslation();
  const toggle = useToggleReminder();
  const remove = useDeleteReminder();

  return (
    <li className="flex items-center gap-3 border-b border-slate-100 py-3 last:border-0 dark:border-slate-700">
      <button
        type="button"
        aria-label={reminder.done ? t('gest:monEquipe.reminders.reopen') : t('gest:monEquipe.reminders.complete')}
        title={reminder.done ? t('gest:monEquipe.reminders.reopen') : t('gest:monEquipe.reminders.complete')}
        disabled={toggle.isPending}
        onClick={() =>
          toggle.mutate({ id: reminder.id, done: !reminder.done }, { onError: (err) => onError(teamErrorCode(err)) })
        }
        className="touch-manipulation rounded-lg p-2 text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-600 dark:hover:bg-slate-700 dark:hover:text-slate-300"
      >
        {reminder.done ? <Undo2 size={16} aria-hidden="true" /> : <Check size={16} aria-hidden="true" />}
      </button>
      <div className="min-w-0 flex-1">
        <p className={`truncate text-field ${reminder.done ? 'text-slate-400 line-through dark:text-slate-500' : 'text-slate-900 dark:text-slate-100'}`}>
          {reminder.title}
        </p>
        {reminder.body ? (
          <p className="truncate text-meta text-slate-500 dark:text-slate-400">{reminder.body}</p>
        ) : null}
        {reminder.dueDate ? (
          <p className="text-meta text-slate-500 dark:text-slate-400">
            {t('gest:monEquipe.reminders.due', { date: reminder.dueDate })}
          </p>
        ) : null}
      </div>
      <button
        type="button"
        aria-label={t('gest:monEquipe.reminders.delete')}
        title={t('gest:monEquipe.reminders.delete')}
        disabled={remove.isPending}
        onClick={() => remove.mutate(reminder.id, { onError: (err) => onError(teamErrorCode(err)) })}
        className="shrink-0 touch-manipulation rounded-lg p-2 text-slate-400 transition-colors hover:bg-red-50 hover:text-red-600 dark:hover:bg-red-950 dark:hover:text-red-400"
      >
        <Trash2 size={16} aria-hidden="true" />
      </button>
    </li>
  );
}

export default function MonEquipePage() {
  const { t, i18n } = useTranslation();
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [objectiveOpen, setObjectiveOpen] = useState(false);
  const [reminderOpen, setReminderOpen] = useState(false);
  const [pdfExporting, setPdfExporting] = useState(false);

  const BASE_URL: string = import.meta.env.VITE_API_URL ?? '';

  // F-152 : deep-link ?team= (le SERVEUR decide si l'id est dans la portee — un id
  // hors portee retombe sur la 1re equipe, il n'est jamais honore ici).
  const teamParam = searchParams.get('team');
  const errorCode = searchParams.get('error'); // F-155

  const q = useMonEquipe(teamParam, !!user);
  const forbidden = q.isError && q.error instanceof ApiForbiddenError;

  function setError(code: string) {
    const next = new URLSearchParams(searchParams);
    next.set('error', code);
    setSearchParams(next, { replace: true });
  }

  function selectTeam(id: string) {
    const next = new URLSearchParams(searchParams);
    next.set('team', id);
    next.delete('error');
    setSearchParams(next);
  }

  const data = q.data;
  const selectedTeam = data?.teams.find((team) => team.id === data.selectedTeamId) ?? null;
  const openReminders = data?.reminders.filter((r) => !r.done) ?? [];
  const submittedCount = data?.roster.filter((m) => m.submitted).length ?? 0;

  async function exportGridPdf(teamId: string, teamName: string) {
    const getToken = getRegisteredAccessToken();
    if (!getToken) {
      setError('forbidden');
      return;
    }
    setPdfExporting(true);
    try {
      const token = await getToken();
      const res = await fetch(
        `${BASE_URL}/api/planification/export-pdf?team=${encodeURIComponent(teamId)}`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      if (!res.ok) throw new Error(String(res.status));
      const blob = await res.blob();
      const slug = teamName.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
      const objectUrl = URL.createObjectURL(blob);
      const anchor = document.createElement('a');
      anchor.href = objectUrl;
      anchor.download = `grille-equipe-${slug || teamId}.pdf`;
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      URL.revokeObjectURL(objectUrl);
    } catch {
      setError('exportPdf');
    } finally {
      setPdfExporting(false);
    }
  }

  return (
    <div className="space-y-5">
      <PageHeader
        title={t('gest:monEquipe.title')}
        description={selectedTeam?.name}
        onRefresh={() => void q.refetch()}
        refreshLoading={q.isFetching}
        actions={
          selectedTeam && !forbidden ? (
            <button
              type="button"
              disabled={pdfExporting}
              onClick={() => void exportGridPdf(selectedTeam.id, selectedTeam.name)}
              className={`${GS_COMPONENTS.btnSecondary} touch-manipulation inline-flex items-center gap-2 disabled:opacity-50`}
            >
              <Download size={16} aria-hidden="true" />
              {t('gest:monEquipe.export.gridPdf')}
            </button>
          ) : undefined
        }
      />

      {forbidden ? (
        <MonEquipeForbidden />
      ) : q.isLoading ? (
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
          <SkeletonCard />
          <SkeletonCard />
          <SkeletonCard />
          <SkeletonCard />
        </div>
      ) : q.isError ? (
        <QueryError message={t('gest:monEquipe.loadError')} onRetry={() => void q.refetch()} />
      ) : data ? (
        <>
          {/* F-155 : banniere ?error= — liseré standard SM-5 (jamais de code brut, GA-21). */}
          {errorCode ? (
            <div
              role="alert"
              className="rounded-xl border-l-4 border-red-500 bg-red-50 p-4 text-field text-red-700 dark:bg-red-950 dark:text-red-300"
            >
              {t(`gest:monEquipe.errors.${errorCode}`, { defaultValue: t('gest:monEquipe.errors.server') })}
            </div>
          ) : null}

          {/* F-154 : selecteur multi-equipes (visible si >1 equipe dans la PORTEE serveur). */}
          {data.teams.length > 1 ? (
            <GsSegmented
              options={data.teams.map((team) => ({ value: team.id, label: team.name }))}
              value={data.selectedTeamId ?? data.teams[0].id}
              onChange={selectTeam}
              ariaLabel={t('gest:monEquipe.teamSelector')}
              variant="scrollable"
            />
          ) : null}

          {/* F-153 : aucune equipe dans la portee -> EmptyState (pas une erreur). */}
          {!selectedTeam ? (
            <EmptyState
              icon={<Users size={48} aria-hidden="true" />}
              title={t('gest:monEquipe.empty.title')}
              description={t('gest:monEquipe.empty.description')}
            />
          ) : (
            <>
              {/* F-158..F-161 : 4 KPI. Cards blanches + icone en pastille. */}
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
                {/* F-158 / M-153 : agregat seul -> planification salariale (L05). */}
                <Link
                  to={`/planification-salariale?team=${encodeURIComponent(selectedTeam.id)}&from=mon-equipe`}
                  className="block touch-manipulation rounded-2xl transition-shadow hover:shadow-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500"
                >
                  <KpiCard
                    icon={Wallet}
                    variant="brand"
                    label={t('gest:monEquipe.kpis.payroll')}
                    value={data.payroll ? formatMoney(i18n.language, data.payroll.total) : '—'}
                  />
                </Link>
                <KpiCard
                  icon={Users}
                  variant="blue"
                  label={t('gest:monEquipe.kpis.headcount')}
                  value={String(data.roster.length)}
                />
                <KpiCard
                  icon={Activity}
                  variant="green"
                  label={t('gest:monEquipe.kpis.surveys')}
                  value={`${submittedCount}/${data.roster.length}`}
                />
                <KpiCard
                  icon={BellRing}
                  variant="brand"
                  label={t('gest:monEquipe.kpis.reminders')}
                  value={String(openReminders.length)}
                />
              </div>

              {/* Transparence du perimetre de la masse (KpiCard reste minimal —
                  label/value/icone : on ne l'etend pas pour un cas d'usage). Les
                  membres sans aucun taux connu sont EXCLUS du total : on l'annonce
                  plutot que de les noyer dans un chiffre faussement complet. */}
              {data.payroll && data.payroll.unpricedCount > 0 ? (
                <p className="text-meta text-slate-500 dark:text-slate-400">
                  {t('gest:monEquipe.kpis.payrollUnpriced', { count: data.payroll.unpricedCount })}
                </p>
              ) : null}

              {/* Anneau sondages (F-163) + lien Sante (F-156, volet porte ICI). */}
              <div className={`${GS_COMPONENTS.card} flex flex-wrap items-center justify-between gap-4 p-5`}>
                <div className="flex items-center gap-4">
                  <ProgressRing
                    value={submittedCount}
                    max={data.roster.length}
                    label={t('gest:monEquipe.surveys.ringLabel', {
                      submitted: submittedCount, total: data.roster.length,
                    })}
                  />
                  <div>
                    <p className="font-semibold text-slate-900 dark:text-slate-100">
                      {t('gest:monEquipe.surveys.title')}
                    </p>
                    <p className="text-meta text-slate-500 dark:text-slate-400">
                      {t('gest:monEquipe.surveys.subtitle', {
                        submitted: submittedCount, total: data.roster.length, period: data.period,
                      })}
                    </p>
                  </div>
                </div>
                <Link
                  to={`/mon-equipe/sante?team=${encodeURIComponent(selectedTeam.id)}`}
                  className={`${GS_COMPONENTS.btnSecondary} touch-manipulation inline-flex items-center gap-2`}
                >
                  <HeartPulse size={16} aria-hidden="true" />
                  {t('gest:monEquipe.surveys.healthLink')}
                  <ArrowRight size={16} aria-hidden="true" />
                </Link>
              </div>

              {/* F-162 : cartes membres -> /profile/{email}?tab=gestion. */}
              <section className="space-y-3">
                <h2 className="text-head font-semibold text-slate-900 dark:text-slate-100">
                  {t('gest:monEquipe.roster.title')}
                </h2>
                {data.roster.length === 0 ? (
                  <EmptyState icon={<Users size={48} aria-hidden="true" />} title={t('gest:monEquipe.roster.empty')} />
                ) : (
                  <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
                    {data.roster.map((member) => (
                      <MemberCard key={member.email} member={member} teamId={selectedTeam.id} />
                    ))}
                  </div>
                )}
              </section>

              <div className="grid grid-cols-1 gap-5 lg:grid-cols-2">
                {/* F-164/F-165 : objectifs d'equipe (cycle M-156). */}
                <section className={`${GS_COMPONENTS.card} p-5`}>
                  <div className="mb-2 flex items-center justify-between gap-3">
                    <h2 className="flex items-center gap-2 font-semibold text-slate-900 dark:text-slate-100">
                      <Target size={18} aria-hidden="true" className="text-brand-500" />
                      {t('gest:monEquipe.objectives.title')}
                    </h2>
                    {data.viewer.canWriteObjectives ? (
                      <button
                        type="button"
                        onClick={() => setObjectiveOpen(true)}
                        className={`${GS_COMPONENTS.btnSecondary} touch-manipulation inline-flex items-center gap-1`}
                      >
                        <Plus size={16} aria-hidden="true" />
                        {t('gest:monEquipe.objectives.add')}
                      </button>
                    ) : null}
                  </div>
                  {data.objectives.length === 0 ? (
                    <p className="py-4 text-field text-slate-400 dark:text-slate-500">
                      {t('gest:monEquipe.objectives.empty')}
                    </p>
                  ) : (
                    <ul>
                      {data.objectives.map((objective) => (
                        <ObjectiveItem
                          key={objective.id}
                          objective={objective}
                          teamId={selectedTeam.id}
                          canWrite={!!data.viewer.canWriteObjectives}
                          onError={setError}
                        />
                      ))}
                    </ul>
                  )}
                </section>

                {/* F-166 : rappels (ownership M-155). */}
                <section className={`${GS_COMPONENTS.card} p-5`}>
                  <div className="mb-2 flex items-center justify-between gap-3">
                    <h2 className="flex items-center gap-2 font-semibold text-slate-900 dark:text-slate-100">
                      <BellRing size={18} aria-hidden="true" className="text-brand-500" />
                      {t('gest:monEquipe.reminders.title')}
                    </h2>
                    <button
                      type="button"
                      onClick={() => setReminderOpen(true)}
                      className={`${GS_COMPONENTS.btnSecondary} touch-manipulation inline-flex items-center gap-1`}
                    >
                      <Plus size={16} aria-hidden="true" />
                      {t('gest:monEquipe.reminders.add')}
                    </button>
                  </div>
                  {data.reminders.length === 0 ? (
                    <p className="py-4 text-field text-slate-400 dark:text-slate-500">
                      {t('gest:monEquipe.reminders.empty')}
                    </p>
                  ) : (
                    <ul>
                      {data.reminders.map((reminder) => (
                        <ReminderItem key={reminder.id} reminder={reminder} onError={setError} />
                      ))}
                    </ul>
                  )}
                </section>
              </div>

              <NewTeamObjectiveDialog
                open={objectiveOpen}
                onClose={() => setObjectiveOpen(false)}
                onError={setError}
                teamId={selectedTeam.id}
              />
              <NewReminderDialog
                open={reminderOpen}
                onClose={() => setReminderOpen(false)}
                onError={setError}
              />
            </>
          )}
        </>
      ) : null}
    </div>
  );
}
